import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, TextField, Typography, Paper, FormControl, Select, Grid, MenuItem, InputLabel, IconButton, Tooltip, Stack, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import UpdateIcon from '@mui/icons-material/Update';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

const supportedTimezones = Intl.supportedValuesOf('timeZone');

const timeAgo = (timestamp, now = Date.now()) => {
  const seconds = Math.floor((now - timestamp) / 1000);
  if (seconds < 5) return "just now";
  let interval = seconds / 31536000;
  if (interval > 1) return Math.floor(interval) + " years ago";
  interval = seconds / 2592000;
  if (interval > 1) return Math.floor(interval) + " months ago";
  interval = seconds / 86400;
  if (interval > 1) return Math.floor(interval) + " days ago";
  interval = seconds / 3600;
  if (interval > 1) return Math.floor(interval) + " hours ago";
  interval = seconds / 60;
  if (interval > 1) return Math.floor(interval) + " minutes ago";
  return Math.floor(seconds) + " seconds ago";
};


function TimestampConverter() {
  const [now, setNow] = useState(new Date());
  const [unit, setUnit] = useState('seconds');
  const [timestamp, setTimestamp] = useState(Math.floor(now.getTime() / 1000).toString());
  const [humanDate, setHumanDate] = useState(now.toISOString().slice(0, 19).replace('T', ' '));
  const [timeZone, setTimeZone] = useState('UTC');
  const [error, setError] = useState('');

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const convertToDate = useCallback((ts, u, tz) => {
    if (!/^\d*$/.test(ts)) {
      setError('Timestamp must be an integer.');
      return '';
    }
    setError('');
    if (ts.trim() === '') return '';

    const multiplier = u === 'seconds' ? 1000 : 1;
    const date = new Date(parseInt(ts, 10) * multiplier);
    return date.toLocaleString('en-CA', { timeZone: tz, hour12: false }).replace(', ', ' ');
  }, []);

  const convertToTimestamp = useCallback((dateStr, u) => {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      setError('Invalid date format.');
      return '';
    }
    setError('');
    const timeValue = date.getTime();
    return u === 'seconds' ? Math.floor(timeValue / 1000).toString() : timeValue.toString();
  }, []);

  useEffect(() => {
    setHumanDate(convertToDate(timestamp, unit, timeZone));
  }, [timestamp, unit, timeZone, convertToDate]);

  const handleTimestampChange = (ts) => {
    setTimestamp(ts);
  };

  const handleHumanDateChange = (dateStr) => {
    setHumanDate(dateStr);
    setTimestamp(convertToTimestamp(dateStr, unit));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const currentTimestamp = unit === 'seconds' ? Math.floor(now.getTime() / 1000) : now.getTime();

  const relativeTime = useMemo(() => {
    if (!timestamp || error) return '–';
    const multiplier = unit === 'seconds' ? 1000 : 1;
    return timeAgo(parseInt(timestamp, 10) * multiplier, now.getTime());
  }, [timestamp, unit, error, now]);

  return (
    <>
      <SEO
        title="Unix Timestamp Converter | Epoch Time Calculator | UmbrellaCo"
        description="Convert Unix epoch timestamps to human-readable dates and vice versa. Supports multiple timezones, seconds, and milliseconds."
        keywords="timestamp converter, unix time, epoch converter, human readable date, timezone converter"
        url="/dev-tools/timestamp-converter"
        jsonLd={createSoftwareAppSchema(
          'Unix Timestamp Converter',
          'Instantly convert between Unix timestamps and human-readable date formats.',
          'https://umbrellaco.in/dev-tools/timestamp-converter',
          ['Time Conversion', 'Epoch Calculator', 'Developer Utilities']
        )}
      />
      <ToolPageContainer
        title="Timestamp Converter"
        description="Translate Unix epoch timestamps into human-readable dates across various timezones, or calculate epoch time from a date string."
      >

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6">Current Epoch Time</Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography fontFamily="monospace" fontSize="1.2rem">{currentTimestamp}</Typography>
          <Tooltip title="Copy Timestamp">
            <IconButton onClick={() => copyToClipboard(currentTimestamp.toString())} size="small">
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <UpdateIcon fontSize="small" color="action" />
        </Stack>
        <Typography variant="body2" color="text.secondary">{now.toUTCString()}</Typography>
      </Paper>

      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <RadioGroup row value={unit} onChange={(e) => setUnit(e.target.value)}>
          <FormControlLabel value="seconds" control={<Radio />} label="Seconds" />
          <FormControlLabel value="milliseconds" control={<Radio />} label="Milliseconds" />
        </RadioGroup>
      </FormControl>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            label={`Unix Timestamp (${unit})`}
            value={timestamp}
            onChange={(e) => handleTimestampChange(e.target.value)}
            error={!!error}
            helperText={error || ' '}
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            label="Human-Readable Date (YYYY-MM-DD HH:MM:SS)"
            value={humanDate}
            onChange={(e) => handleHumanDateChange(e.target.value)}
            fullWidth
            helperText=" "
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl fullWidth>
            <InputLabel>Timezone</InputLabel>
            <Select value={timeZone} label="Timezone" onChange={(e) => setTimeZone(e.target.value)}>
              {supportedTimezones.map(tz => <MenuItem key={tz} value={tz}>{tz}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body1"><strong>Relative Time:</strong> {relativeTime}</Typography>
        </Grid>
      </Grid>
      </ToolPageContainer>
    </>
  );
}

export default TimestampConverter;